import { initSentry } from './lib/sentry';
initSentry();

import { initAnalytics } from './lib/analytics';
initAnalytics();

import './lib/installManager';

// Fires Subscribe (FB pixel + CAPI + Keitaro install postback) the first time
// the app boots in standalone mode after an ad-attributed install. No-op for
// organic loads or repeat launches — the helper dedupes via localStorage flag.
import { trackInstallIfFirstStandaloneRun } from './lib/installAttribution';
trackInstallIfFirstStandaloneRun();

// 2026-05-12 — verifiable install detection. Wires the `appinstalled` event
// + first standalone-mode visit to POST /api/install-detected so the backend
// can flip users.installed_at + install_source. Replaces the old
// self-reported "I've Installed It!" funnel (which inflated install counts
// since the button counted as an install whether they installed or not).
import { setupInstallDetection } from './lib/installDetection';
setupInstallDetection();

import { recordPushOpen } from './lib/pushTracking';

// Capture push-open notification type from URL param (set by service worker
// on push tap). Paired with checkoutTracking.ts which reads this back via
// getRecentPushOpen() to attribute checkout clicks within the TTL window.
{
  const params = new URLSearchParams(window.location.search);
  const pushType = params.get('papacito_push');
  if (pushType) {
    recordPushOpen(pushType);
    const cleanUrl = new URL(window.location.href);
    cleanUrl.searchParams.delete('papacito_push');
    window.history.replaceState({}, '', cleanUrl.toString());
  }
}

// VAPID parity check — logs only the first 12 chars of the public key so we
// can confirm same keypair on frontend + backend in browser devtools without
// leaking the full key. Paired with an equivalent log in artifacts/api-server/
// src/index.ts.
{
  const vapidPub = (import.meta as any).env?.VITE_VAPID_PUBLIC_KEY;
  if (typeof vapidPub === 'string' && vapidPub.length > 0) {
    console.log('[VAPID]', vapidPub.substring(0, 12));
  } else {
    console.warn('[VAPID] VITE_VAPID_PUBLIC_KEY is not set — push subscribe will fail.');
  }
}

if (!localStorage.getItem('papacito_session_start')) {
  localStorage.setItem('papacito_session_start', Date.now().toString());
}

let chunkReloadAttempted = false;

function isChunkError(msg: string | undefined): boolean {
  if (!msg) return false;
  const lower = msg.toLowerCase();
  return (
    lower.includes('failed to fetch dynamically imported module') ||
    lower.includes('loading chunk') ||
    lower.includes('importing a module script failed') ||
    lower.includes('error loading dynamically imported module') ||
    lower.includes('load failed')
  );
}

function clearSwCachesAndReload() {
  const reload = () => location.reload();
  if (typeof caches !== 'undefined') {
    caches.keys().then((names) =>
      Promise.all(names.map((name) => caches.delete(name)))
    ).then(reload).catch(reload);
  } else {
    reload();
  }
}

window.addEventListener('error', (event) => {
  if (chunkReloadAttempted) return;
  if (isChunkError(event.message)) {
    chunkReloadAttempted = true;
    clearSwCachesAndReload();
  }
});

window.addEventListener('unhandledrejection', (event) => {
  if (chunkReloadAttempted) return;
  const reason = event.reason;
  if (isChunkError(reason?.message) || isChunkError(String(reason))) {
    chunkReloadAttempted = true;
    event.preventDefault();
    clearSwCachesAndReload();
  }
});

if ('serviceWorker' in navigator) {
  navigator.serviceWorker.getRegistrations().then((registrations) => {
    registrations.forEach((reg) => {
      reg.update();
    });
  });
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    window.location.reload();
  });
}

import { createRoot } from 'react-dom/client';
import App from './App';
import './index.css';

// If a stale service worker hijacked a server-only route (the /install
// ad lander, /install-app organic email lander, or /r/* checkout
// redirect), don't render <App /> — wouter would flash its 404 before
// the reg.update() + controllerchange handler above reloads us into the
// freshly-activated SW. Mirrors the SW denylist in vite.config.ts. Only
// triggers when an SW is actually controlling the page, so dev and
// no-SW contexts still get normal SPA behavior.
const path = window.location.pathname;
const onServerOnlyPath =
  /^\/install(\/|$|\?)/.test(path) ||
  /^\/install-app(\/|$|\?)/.test(path) ||
  /^\/r\//.test(path);
const swControlling =
  typeof navigator !== 'undefined' && !!navigator.serviceWorker?.controller;

if (onServerOnlyPath && swControlling) {
  // Safety net if the SW update stalls and controllerchange never fires.
  setTimeout(clearSwCachesAndReload, 5000);
} else {
  createRoot(document.getElementById('root')!).render(<App />);
}
